import { inject } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { catchError, of, throwError } from 'rxjs';
import { AuthService } from '@auth/services/auth.service';
import { FeedbackService } from '../services/feedback.service';
import { extractErrorMessage } from '../utils/error-parser.util';

/**
 * Token de contexto para requisições que queiram suprimir o toast de erro global
 * Exemplo de uso no HttpClient:
 * this.http.get(url, { context: new HttpContext().set(SKIP_GLOBAL_ERROR_TOAST, true) })
 */
export const SKIP_GLOBAL_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const feedback = inject(FeedbackService);
  const auth = inject(AuthService);

  // Login e primeiro login tratam e mostram o próprio erro com destaque no formulário
  const isAuthPublicRequest =
    req.url.includes('/Auth/login') || req.url.includes('/Auth/loginFirst');

  const skipGlobalToast = req.context.get(SKIP_GLOBAL_ERROR_TOAST);

  return next(req).pipe(
    catchError((err) => {
      // O HttpClient transforma falhas de parsing em HttpErrorResponse mesmo
      // quando o servidor respondeu 2xx. Isto acontece sobretudo em endpoints
      // de comando que devolvem texto ou body vazio em vez de JSON. Não é uma
      // falha da operação e não deve gerar toast vermelho.
      if (isSuccessfulParseError(err)) {
        return of(
          new HttpResponse({
            body: err.error?.text ?? err.error ?? null,
            headers: err.headers,
            status: err.status,
            statusText: err.statusText,
            url: err.url ?? undefined,
          }),
        );
      }

      const detail = extractErrorMessage(err);

      // Tratamento de sessão expirada (401)
      if (err instanceof HttpErrorResponse && err.status === 401 && !isAuthPublicRequest) {
        auth.logout();
      }

      // Exibir toast global se não for requisição de autenticação pública e não estiver suprimido
      if (!isAuthPublicRequest && !skipGlobalToast) {
        feedback.showError(err, {
          summary: getErrorSummary(err),
          detail,
          life: 5500,
        });
      }

      return throwError(() => err);
    }),
  );
};

function getErrorSummary(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    if (!navigator.onLine || err.status === 0) return 'Erro de Ligação';
    if (err.status === 401) return 'Sessão Expirada';
    if (err.status === 403) return 'Acesso Negado';
    if (err.status === 404) return 'Não Encontrado';
    if (err.status === 409) return 'Conflito de Dados';
    if (err.status === 422) return 'Validação de Dados';
    if (err.status >= 500) return 'Erro do Servidor';
  }
  return 'Erro';
}

function isSuccessfulParseError(err: unknown): err is HttpErrorResponse {
  return (
    err instanceof HttpErrorResponse &&
    err.status >= 200 &&
    err.status < 300 &&
    (err.error?.error instanceof SyntaxError ||
      typeof err.error?.text === 'string' ||
      String(err.message ?? '').toLowerCase().includes('parsing'))
  );
}

